import { motion } from "framer-motion";
import { Link2, UserPlus, Gift, Download } from "lucide-react";
import { Mole } from "../components/Mole";
import { Reveal, Chapter } from "../components/Reveal";
import { TelegramShareButton } from "../components/TelegramShareButton";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const STEPS = [
  { n: "01", icon: Link2, color: "text-nora-accent", title: "Скопируйте свою ссылку" },
  { n: "02", icon: UserPlus, color: "text-nora-pink", title: "Друг входит в NORA по ссылке, там его ждет инструкция" },
  { n: "03", icon: Gift, color: "text-nora-mint", title: "Вы оба получаете бесплатные дни" },
];

const FAQ = [
  {
    q: "Как получить бонусные дни?",
    a: "Скопируйте свою ссылку и отправьте другу. Как только он войдет в NORA по ней, дни начислятся автоматически.",
  },
  {
    q: "Сколько друзей можно пригласить?",
    a: "Приглашайте столько друзей, сколько хотите. Если появятся ограничения, мы обязательно предупредим заранее.",
  },
  {
    q: "Бонусы суммируются?",
    a: "Нет: пришедшему по ссылке — 3 дня, пригласившему — 5 дней, а новичку без ссылки — 1 день.",
  },
  {
    q: "Куда попадают дни?",
    a: "На ваш аккаунт автоматически — сразу после того, как друг войдет по ссылке.",
  },
];

export default function Referral({ onCta }) {
  return (
    <main>
      <section className="relative overflow-hidden pb-8 pt-28 sm:pt-36">
        <div
          aria-hidden="true"
          className="absolute -left-24 top-24 h-72 w-72 rounded-full bg-nora-pink/10 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="absolute -right-24 top-40 h-80 w-80 rounded-full bg-nora-mint/10 blur-3xl"
        />
        <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6">
          <motion.div
            className="mx-auto w-24 sm:w-28"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 60, damping: 14, delay: 0.2 }}
          >
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            >
              <Mole data-testid="referral-mascot" variant="gift" className="w-full" />
            </motion.div>
          </motion.div>
          <Reveal delay={0.1}>
            <h1
              data-testid="referral-title"
              className="mt-6 font-display text-4xl font-black leading-[1.1] tracking-tight sm:text-5xl"
            >
              Приглашайте друзей, получайте оба
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p
              data-testid="referral-subtitle"
              className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-nora-ink/70 md:text-lg"
            >
              Поделитесь ссылкой с другом: вы оба получите бесплатные дни NORA
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-10 sm:py-14">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <Chapter n="01">Как это работает</Chapter>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.n} delay={0.1 + i * 0.12}>
                  <div
                    data-testid={`referral-step-${i + 1}`}
                    className="relative h-full overflow-hidden rounded-[2rem] border border-nora-ink/5 bg-nora-surface p-8 shadow-sm transition-transform duration-300 hover:-translate-y-2"
                  >
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none absolute -right-2 -top-6 font-display text-8xl font-black opacity-10 ${s.color}`}
                    >
                      {s.n}
                    </span>
                    <Icon className={`h-8 w-8 ${s.color}`} />
                    <h3 className="mt-5 font-display text-lg font-bold leading-snug md:text-xl">
                      {s.title}
                    </h3>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-10 sm:py-14">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <Chapter n="02">Условия</Chapter>
          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            <Reveal>
              <div
                data-testid="referral-card-friend"
                className="h-full rounded-[2rem] border border-nora-pink/25 bg-nora-pink/10 p-8 text-center sm:p-10"
              >
                <p className="leading-relaxed text-nora-ink/70">
                  Друг, который вошёл по вашей ссылке
                </p>
                <p className="mt-4 font-display text-5xl font-black tracking-tight text-nora-pink">
                  3 дня
                </p>
                <p className="mt-2 font-display text-sm font-bold">бесплатно</p>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div
                data-testid="referral-card-you"
                className="h-full rounded-[2rem] border-2 border-nora-accent bg-nora-accent/10 p-8 text-center shadow-xl shadow-nora-accent/10 sm:p-10"
              >
                <p className="leading-relaxed text-nora-ink/70">
                  Вы, если вошли по вашей ссылке
                </p>
                <p className="mt-4 font-display text-5xl font-black tracking-tight text-nora-accent">
                  5 дней
                </p>
                <p className="mt-2 font-display text-sm font-bold">бесплатно</p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p
              data-testid="referral-notice"
              className="mt-6 rounded-[1.5rem] border border-nora-ink/10 bg-nora-surface px-6 py-4 text-center text-sm leading-relaxed text-nora-ink/60"
            >
              Новым пользователям без ссылки доступен 1 день бесплатно. Бонусы
              не суммируются
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-10 sm:py-14">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <Chapter n="03">Вопросы</Chapter>
          <Reveal delay={0.1}>
            <Accordion
              type="single"
              collapsible
              data-testid="referral-faq-accordion"
              className="mt-8 rounded-[2rem] border border-nora-ink/5 bg-nora-surface px-6 py-2 shadow-sm sm:px-8"
            >
              {FAQ.map((item, i) => (
                <AccordionItem
                  key={item.q}
                  value={`rfaq-${i + 1}`}
                  data-testid={`referral-faq-item-${i + 1}`}
                  className="border-nora-ink/10"
                >
                  <AccordionTrigger
                    data-testid={`referral-question-${i + 1}`}
                    className="py-5 text-left font-display text-base font-bold hover:no-underline md:text-lg [&>svg]:text-nora-accent"
                  >
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="leading-relaxed text-nora-ink/70">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 pt-6 text-center sm:pb-24">
        <Reveal>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <motion.button
              type="button"
              data-testid="referral-download-button"
              onClick={onCta}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
              className="inline-flex items-center gap-2.5 rounded-full bg-nora-accent px-8 py-4 font-display text-sm font-bold text-[#1F2730] shadow-xl shadow-nora-accent/25 transition-colors hover:bg-nora-accenthover sm:text-base"
            >
              <Download className="h-5 w-5" />
              Скачать NORA
            </motion.button>
            <TelegramShareButton
              testId="referral-share-telegram"
              url={`${window.location.origin}/referral`}
              text="Приглашайте друзей в НОРУ — вы оба получите бесплатные дни. VPN в одну кнопку, 1 день бесплатно."
            />
          </div>
        </Reveal>
      </section>
    </main>
  );
}
